import type {
  NoInfer,
  OnChangeFn,
  PaginationState,
  SortingState,
  VisibilityState,
} from "@tanstack/table-core";
import { isFunction } from "lodash-es";
import { useCallback, useMemo } from "react";

/**
 * リストの空データ
 */
export const LIST_EMPTY_DATA = {
  items: [],
  length: 0,
  count: 0,
  page: 1,
  maxPage: 1,
};

/**
 * ページネーションをクエリパラメータとして扱うためのフック
 * @param options.search 現在のクエリパラメータ
 * @param options.onSearchChange クエリパラメータの変更時のコールバック
 * @returns ページネーションの状態と変更関数
 * @example
 * const [pagination, setPagination] = usePaginationSearch({
 *   search: Route.useSearch(),
 *   onSearchChange: (search) => navigate({ search }),
 * });
 * console.log(pagination); // { pageIndex: 0, pageSize: 10 }
 * setPagination({ pageIndex: 1, pageSize: 20 });
 * // => navigate({ search: { pageIndex: 1, pageSize: 20 } });
 */
export function usePaginationSearch<TSearch extends PaginationState>(options: {
  search: TSearch;
  onSearchChange: (search: TSearch) => void;
}) {
  const pagination = useMemo<PaginationState>(
    () => ({
      pageIndex: options.search.pageIndex,
      pageSize: options.search.pageSize,
    }),
    [options.search],
  );

  const setPagination = useCallback<OnChangeFn<PaginationState>>(
    (updaterOrValue) => {
      const { pageIndex, pageSize } = isFunction(updaterOrValue)
        ? updaterOrValue(pagination)
        : updaterOrValue;
      options.onSearchChange({ ...options.search, pageIndex, pageSize });
    },
    [options, pagination],
  );

  return [pagination, setPagination] as const;
}

/**
 * ソーティングをクエリパラメータとして扱うためのフック
 * @param options.search 現在のクエリパラメータ
 * @param options.onSearchChange クエリパラメータの変更時のコールバック
 * @returns ソーティングの状態と変更関数
 * @example
 * const [sorting, setSorting] = useSortingSearch({
 *   search: Route.useSearch(),
 *   onSearchChange: (search) => navigate({ search }),
 * });
 * console.log(sorting); // [{ id: "name", desc: false }]
 * setSorting([{ id: "name", desc: true }]);
 * // => navigate({ search: { column: "name", order: "desc" } });
 */
export function useSortingSearch<
  TSearch extends {
    sortColumn?: string;
    sortOrder?: "asc" | "desc";
  },
>(options: {
  search: TSearch;
  onSearchChange: (search: TSearch) => void;
  convertSortColumn?: (sortColumn: string) => string;
}) {
  const { sortColumn, sortOrder } = options.search;

  const sorting = useMemo<SortingState>(() => {
    if (sortColumn && sortOrder)
      return [
        {
          id: options.convertSortColumn
            ? options.convertSortColumn(sortColumn)
            : sortColumn,
          desc: sortOrder === "desc",
        },
      ];
    return [];
  }, [sortColumn, sortOrder, options.convertSortColumn]);

  const setSorting = useCallback<OnChangeFn<SortingState>>(
    (updaterOrValue) => {
      const newSorting = isFunction(updaterOrValue)
        ? updaterOrValue(sorting)
        : updaterOrValue;
      const [sort] = newSorting;

      // ソートがクリアされた場合(空配列)、sortColumnとsortOrderをundefinedにする
      if (!sort) {
        options.onSearchChange({
          ...options.search,
          sortColumn: undefined,
          sortOrder: undefined,
        });
        return;
      }

      options.onSearchChange({
        ...options.search,
        sortColumn: sort.id,
        sortOrder: sort.desc ? "desc" : "asc",
      });
    },
    [options, sorting],
  );

  return [sorting, setSorting] as const;
}

/**
 * タブフィルタに対応する列の表示状態を作成する
 *
 * @example
 * createTabFilterVisibilityState({
 *   columns: [
 *     "student",
 *     "grade",
 *     "attendance",
 *     "absence",
 *     "actions",
 *   ],
 *   permanentColumns: [
 *    "student",
 *    "actions",
 *   ],
 *   tabFilterToColumns: {
 *     attendance: ["attendance", "absence"],
 *     all: ["student", "grade", "attendance", "absence"],
 *   },
 * });
 * // => {
 * //   attendance: {
 * //     student: true,
 * //     grade: false,
 * //     attendance: true,
 * //     absence: true,
 * //     actions: true,
 * //   },
 * //   all: {
 * //     student: true,
 * //     grade: true,
 * //     attendance: true,
 * //     absence: true,
 * //     actions: true,
 * //   },
 * // }
 */
export function createTabFilterVisibilityState<
  TTabKey extends string,
  TColumns extends string,
  TPermanentColumns extends TColumns,
>(options: {
  /** 全ての列ID */
  columns: TColumns[];
  /** 常に表示する列ID */
  permanentColumns: TPermanentColumns[];
  /** フィルタタブに対応する列ID */
  tabFilterToColumns: Record<
    TTabKey,
    NoInfer<Exclude<TColumns, TPermanentColumns>>[]
  >;
}): Record<TTabKey, VisibilityState> {
  const { columns, permanentColumns, tabFilterToColumns } = options;
  const tabFilterVisibilityState = {} as Record<TTabKey, VisibilityState>;
  for (const tab of Object.keys(tabFilterToColumns) as TTabKey[]) {
    const cols = {} as VisibilityState;
    for (const column of columns) {
      cols[column] = false;
    }
    for (const column of tabFilterToColumns[tab]) {
      cols[column] = true;
    }
    for (const column of permanentColumns) {
      cols[column] = true;
    }
    tabFilterVisibilityState[tab] = cols;
  }
  return tabFilterVisibilityState;
}

/**
 * ページネーションされたデータの型
 */
type PaginatedData<TData> = {
  items: TData[];
  count: number;
  length: number;
  maxPage: number;
  page: number;
};

/**
 * 複数のページネーションされたデータを1つにマージする関数
 *
 * @template TData - データの型
 * @param {PaginatedData<TData>[]} pages - マージするページネーションされたデータの配列
 * @returns {PaginatedData<TData>} マージされたページネーションデータ
 *
 * @example
 * const page1 = { items: [1, 2], count: 2, length: 2, maxPage: 3, page: 1 };
 * const page2 = { items: [3, 4], count: 2, length: 2, maxPage: 3, page: 2 };
 * const merged = mergePaginatedData([page1, page2]);
 * console.log(merged);
 * // 出力:
 * // {
 * //   items: [1, 2, 3, 4],
 * //   count: 4,
 * //   length: 4,
 * //   maxPage: 3,
 * //   page: 2
 * // }
 */
export function mergePaginatedData<TData>(pages: PaginatedData<TData>[]) {
  return pages.reduce(
    (acc, data) => ({
      items: acc.items.concat(data.items),
      count: acc.count + data.count,
      length: acc.length + data.length,
      maxPage: Math.max(acc.maxPage, data.maxPage),
      page: data.page,
    }),
    { items: [], count: 0, length: 0, maxPage: 0, page: 0 }, // 初期値
  );
}
